import { setBatchSummaryTestId } from "@bcc/components/batches/batch-summary.utils";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { setSidebarNavTourId, TOUR_IDS } from "../tour-ids";
import type { FeatureTour } from "../tour.types";
import { DATA_TABLE_ROW_SELECTOR, testIdSelector, tourTargetSelector } from "../tour.utils";

/**
 * Fallback for steps whose target may legitimately never render (page-header
 * controls in the showcase); dependent steps below skip instantly via `skipUnless`.
 */
const SELECTED_BATCH_STEP_TIMEOUT_MS = 4000;

const BATCHES_TABLE = tourTargetSelector(TOUR_IDS.batches.table);
/** Any batch row — the summary is only worth showing when there is something to select */
const BATCH_ROW = `${BATCHES_TABLE} ${DATA_TABLE_ROW_SELECTOR}`;
/** Rendered only while a batch is selected — totals and document actions hang off it */
const SELECTED_BATCH = testIdSelector(setBatchSummaryTestId("batch-id"));

export const batchesTour: FeatureTour = {
  id: "batches",
  titleKey: "tour.features.batches.title",
  order: 3,
  requiredModule: "batch",
  requiredPage: "batch.view",
  steps: [
    {
      id: "batches.nav",
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(setSidebarNavTourId("batch")),
      titleKey: "tour.steps.batches.nav.title",
      bodyKey: "tour.steps.batches.nav.body",
      placement: "right",
    },
    {
      id: "batches.search-filters",
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(TOUR_IDS.batches.statusFilters),
      // The search box lives in the page header, in a separate subtree from
      // the status chips — spotlight both (chips alone when the header is absent)
      extendTargets: [tourTargetSelector(TOUR_IDS.batches.search)],
      titleKey: "tour.steps.batches.searchFilters.title",
      bodyKey: "tour.steps.batches.searchFilters.body",
      placement: "bottom",
    },
    {
      id: "batches.table",
      showsSampleData: true,
      route: ROUTE_PATHS.BATCHES,
      target: BATCHES_TABLE,
      titleKey: "tour.steps.batches.table.title",
      bodyKey: "tour.steps.batches.table.body",
      placement: "top",
      emptyState: {
        detectTarget: BATCH_ROW,
        bodyKey: "tour.steps.batches.table.emptyBody",
      },
    },
    {
      id: "batches.summary",
      showsSampleData: true,
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(TOUR_IDS.batches.summary),
      titleKey: "tour.steps.batches.summary.title",
      bodyKey: "tour.steps.batches.summary.body",
      placement: "bottom",
      // With no batches at all the table step has already explained the page
      skipUnless: BATCH_ROW,
      emptyState: {
        detectTarget: SELECTED_BATCH,
        bodyKey: "tour.steps.batches.summary.emptyBody",
      },
    },
    {
      id: "batches.metrics",
      showsSampleData: true,
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(TOUR_IDS.batches.summaryMetrics),
      titleKey: "tour.steps.batches.metrics.title",
      bodyKey: "tour.steps.batches.metrics.body",
      placement: "bottom",
      skipUnless: SELECTED_BATCH,
      targetTimeoutMs: SELECTED_BATCH_STEP_TIMEOUT_MS,
    },
    {
      id: "batches.print-actions",
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(TOUR_IDS.batches.printActions),
      titleKey: "tour.steps.batches.printActions.title",
      bodyKey: "tour.steps.batches.printActions.body",
      placement: "left",
      skipUnless: SELECTED_BATCH,
      targetTimeoutMs: SELECTED_BATCH_STEP_TIMEOUT_MS,
    },
    {
      id: "batches.export",
      route: ROUTE_PATHS.BATCHES,
      target: tourTargetSelector(TOUR_IDS.batches.export),
      titleKey: "tour.steps.batches.export.title",
      bodyKey: "tour.steps.batches.export.body",
      placement: "bottom",
      targetTimeoutMs: SELECTED_BATCH_STEP_TIMEOUT_MS,
    },
  ],
};
